import { useEffect, useRef } from 'react';
import { BusyLabel } from './busy-label.js';

/**
 * The warning before the first parent removes the other (canvas row D, "the
 * warning panel", approved 17 October), built as the video warning is
 * (remove-video-sheet.tsx): the page dims, the panel says what removing does,
 * Remove and Keep.
 *
 * Keep has focus on opening, so a stray Enter keeps them; Escape and a tap on
 * the dimmed page keep them too. Focus goes back where it was after.
 */
export function RemoveParentSheet({
  first,
  working,
  failed,
  onRemove,
  onKeep,
}: {
  first: string;
  working: boolean;
  failed: boolean;
  onRemove: () => void;
  onKeep: () => void;
}) {
  const keep = useRef<HTMLButtonElement>(null);
  const keepNow = useRef(onKeep);
  keepNow.current = onKeep;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    keep.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') keepNow.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus();
    };
  }, []);

  return (
    <div className="sheet-dim" onClick={() => !working && onKeep()}>
      <div
        className="sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="remove-parent-title"
        aria-describedby="remove-parent-warning"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="remove-parent-title">Remove {first} from the family?</h3>
        <p className="sheet-warning" id="remove-parent-warning">
          {first} will no longer see your family&rsquo;s lessons or be able to book.
        </p>
        {failed ? (
          <p className="form-note" role="alert">
            That did not work. Try again.
          </p>
        ) : null}
        <button type="button" className="warn-fill" onClick={onRemove} disabled={working} aria-busy={working}>
          <BusyLabel busy={working} working="Removing…">
            Remove
          </BusyLabel>
        </button>
        <button ref={keep} type="button" className="secondary" onClick={onKeep} disabled={working}>
          Keep
        </button>
      </div>
    </div>
  );
}
